import React, { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import usersRequest from "../../request/usersRequest";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import PasswordField from "../common/passwordField/passwordField";
import logo from "../../images/LogoFullText.png";
import "./signinPage.css";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";

const SignInPage = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [loading, setLoading] = useState(false);
    const [banner, setBanner] = useState(null);
    const [exiting, setExiting] = useState(false);

    const { setUserData, language } = useContext(CombinedContext);
    const navigate = useNavigate();

    const showBanner = (type, text, duration = 4000) => {
        setBanner({ type, text });
        setExiting(false);
        setTimeout(() => setExiting(true), duration - 250);
        setTimeout(() => {
            setBanner(null);
            setExiting(false);
        }, duration);
    };

    const getLoginError = (details) => {
        if (details?.includes("Incorrect username or password") || details?.includes("Invalid login")) {
            return { es: "Correo o contraseña incorrectos", en: "Incorrect username or password" };
        }
        if (details?.includes("User is not confirmed")) {
            return { es: "Cuenta no verificada", en: "Account not verified" };
        }
        if (details?.includes("User does not exist")) {
            return { es: "Usuario no encontrado", en: "User not found" };
        }
        return { es: "Error al iniciar sesión", en: "Error logging in" };
    };

    const handleLogin = async () => {
        setLoading(true);

        try {
            const data = await usersRequest.login({ email, password });

            const formattedData = {
                ...data,
                cognitoTokens: {
                    IdToken: data.cognitoTokens?.IdToken || data.IdToken,
                    AccessToken: data.cognitoTokens?.AccessToken || data.AccessToken,
                    RefreshToken: data.cognitoTokens?.RefreshToken || data.RefreshToken,
                },
            };

            setUserData(formattedData);

            const storage = rememberMe ? localStorage : sessionStorage;
            storage.setItem("idToken", formattedData.cognitoTokens.IdToken);
            storage.setItem("accessToken", formattedData.cognitoTokens.AccessToken);
            if (formattedData.cognitoTokens.RefreshToken) {
                storage.setItem("refreshToken", formattedData.cognitoTokens.RefreshToken);
            }
            storage.setItem("user", JSON.stringify(formattedData.user));
            if (formattedData.shop) {
                storage.setItem("shop", JSON.stringify(formattedData.shop));
            }

            if (
                formattedData.user?.role === "admin" ||
                formattedData.user?.role === "owner" ||
                formattedData.user?.role === "staff"
            ) {
                return navigate("/calendar");
            }

            navigate("/");
        } catch (err) {
            console.error("Login failed:", err);
            // setError(err.message || "Invalid email or password");
            const msg = getLoginError(err.details);
            showBanner("error", language === "es" ? msg.es : msg.en);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleLogin();
    };

    return (
        <div className="sf-si-root">
            <div className="sf-si-card">
                {/* Title */}
                <h1 className="sf-si-title">{translations[language].signIn.title}</h1>

                {/* Email */}
                <div className="sf-si-field">
                    <label className="sf-si-label">{translations[language].signIn.email}</label>
                    <input
                        className="sf-si-input"
                        type="email"
                        placeholder={translations[language].signIn.email}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={handleKeyDown}
                        autoComplete="email"
                    />
                </div>

                {/* Password */}
                <div className="sf-si-field">
                    <div className="sf-si-label-row">
                        <label className="sf-si-label">{translations[language].signIn.password}</label>
                        <Link to="/forgot-password" className="sf-si-forgot">
                            {translations[language].signIn.forgotPassword}
                        </Link>
                    </div>
                    <PasswordField
                        value={password}
                        onChange={setPassword}
                        placeholder={translations[language].signIn.password}
                        autoComplete="current-password"
                        onKeyDown={handleKeyDown}
                    />
                </div>

                {/* Remember me */}
                <div className="sf-si-remember">
                    <input
                        type="checkbox"
                        id="rememberMe"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="sf-si-checkbox"
                    />
                    <label htmlFor="rememberMe" className="sf-si-remember-label">
                        {translations[language].signIn.rememberMe}
                    </label>
                </div>


                {/* Submit */}
                <button
                    className="sf-si-submit"
                    onClick={handleLogin}
                    disabled={loading}
                >
                    {loading ? translations[language].signIn.loading : translations[language].signIn.button}
                </button>

                {/* Footer */}
                <div className="sf-si-footer">
                    <p className="sf-si-footer-text">{translations[language].signIn.noAccount}</p>
                    <Link to="/register" className="sf-si-link">{translations[language].signIn.createCustomer}</Link>
                    <Link to="/listyourshop" className="sf-si-link sf-si-link-muted">{translations[language].signIn.registerBusiness}</Link>
                </div>
            </div>
            {banner && <BannerMessageFixed type={banner.type} text={banner.text} exiting={exiting} />}
        </div>
    );
};

export default SignInPage;
